import { RubyGemMark } from '../components/RubyGemMark';
import { RubyLoader } from '../components/RubyLoader';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Users, DollarSign, TrendingUp, Settings, Copy, Check,
  UserPlus, RefreshCw, ChevronRight, Clock, CheckCircle, XCircle, Ban, Link,
  AlertTriangle
} from 'lucide-react';
import { api } from '../services/apiService';
import { academyApi } from '../services/academyApi';
import type { AcademyTrack } from '@platform/shared-types';
import { merchantAppUrl } from '../utils/merchantUrl';

interface Affiliate {
  id: string;
  name: string;
  email: string;
  referralCode: string;
  status: string;
  totalReferrals: number;
  conversions: number;
  totalEarnings: number;
  createdAt: number;
  /** RubyTill internal ISO — may be given merchant-processing orders. */
  isInternalIso?: boolean;
  isoCapabilities?: { mccCodes?: string[]; states?: string[]; acceptingOrders?: boolean; note?: string };
}

interface AffiliateSettings {
  enabled: boolean;
  approvalMode: 'manual' | 'auto' | 'exam';
  commissionMode: 'percentage' | 'flat' | 'both';
  flatFee: number;           // in cents
  percentageRate: number;    // percentage (e.g., 20 = 20%)
  trialDays: number;
  minPayoutAmount: number;   // in cents
  clearingDays: number;      // days before commission becomes available
  autoApprove?: boolean;     // legacy field
}

interface Analytics {
  overview: {
    totalAffiliates: number;
    activeAffiliates: number;
    pendingAffiliates: number;
    suspendedAffiliates: number;
  };
  earnings: {
    total: number;
    pending: number;
    paid: number;
  };
  referrals: {
    total: number;
    conversions: number;
    conversionRate: string;
  };
  topPerformers: {
    byReferrals: Array<{ id: string; name: string; referralCount: number; totalEarnings: number }>;
    byEarnings: Array<{ id: string; name: string; referralCount: number; totalEarnings: number }>;
  };
  // Legacy fields for backwards compatibility
  totalAffiliates?: number;
  activeAffiliates?: number;
  totalReferrals?: number;
  totalConversions?: number;
  totalCommissionsPaid?: number;
}

// Status badge helper
const getStatusBadge = (status: string) => {
  switch (status) {
    case 'approved':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
          <CheckCircle className="w-3 h-3" /> Approved
        </span>
      );
    case 'pending':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
          <Clock className="w-3 h-3" /> Pending
        </span>
      );
    case 'suspended':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
          <Ban className="w-3 h-3" /> Suspended
        </span>
      );
    case 'rejected':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-600 border border-stone-200">
          <XCircle className="w-3 h-3" /> Rejected
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-600 border border-stone-200">
          {status}
        </span>
      );
  }
};

const SuperAdminAffiliates: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [kindFilter, setKindFilter] = useState<'' | 'referral' | 'iso'>('');
  const [isoBusyId, setIsoBusyId] = useState<string | null>(null);
  const [isoError, setIsoError] = useState<string | null>(null);
  const [settings, setSettings] = useState<AffiliateSettings | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  // The certification catalogue, purely so the 'Certification required'
  // option can warn when choosing it would strand every new affiliate.
  // `null` means "we could not load it" — NOT "there is nothing wrong":
  // the warning is suppressed in that case rather than an all-clear shown,
  // and the option itself is never blocked on this loading.
  const [certTracks, setCertTracks] = useState<AcademyTrack[] | null>(null);

  // Frontend URL for referral links (not super-admin URL)
  const FRONTEND_URL = merchantAppUrl();

  const copyToClipboard = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const copyReferralLink = async (code: string) => {
    const link = `${FRONTEND_URL}/#/signup?ref=${code}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(code);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [affiliatesRes, settingsRes, analyticsRes, tracksRes] = await Promise.all([
        api.affiliates.admin.list(),
        api.affiliates.admin.getSettings(),
        api.affiliates.admin.getAnalytics(),
        // Best-effort and deliberately last: academy-service being down must
        // never stop an admin loading or saving affiliate settings, so this
        // swallows its own failure and leaves the catalogue unknown.
        academyApi.listTracks().catch(() => null),
      ]);
      setAffiliates((affiliatesRes as any)?.affiliates || []);
      setSettings(settingsRes as AffiliateSettings);
      setAnalytics(analyticsRes as Analytics);
      setCertTracks(tracksRes ? (tracksRes.tracks || []) : null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async () => {
    if (!settings) return;
    setActionLoading('settings');
    try {
      await api.affiliates.admin.updateSettings(settings);
      setShowSettingsModal(false);
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to update settings');
    } finally {
      setActionLoading(null);
    }
  };

  // The misconfiguration the design calls out: approvalMode 'exam' with no
  // PUBLISHED, unarchived certification carrying `activatesPartner` means a
  // new affiliate stays pending forever — passing an exam can never activate
  // them, because no exam is wired to activation. It is a legitimate
  // intermediate state while a super admin sets things up, so it is warned
  // about, never blocked. Strictly false only when the catalogue actually
  // loaded: an unknown catalogue must not manufacture an all-clear.
  const noActivatingCertification = certTracks !== null
    && !certTracks.some((t) => t.activatesPartner === true && t.published && t.archived !== true);

  /**
   * Flipping the ISO toggle. Optimistic, because the row is a switch and a
   * switch that waits half a second to move reads as broken — but it is put
   * BACK on failure, and the server's refusal is shown rather than swallowed
   * (an unapproved partner cannot be an ISO, and the reason matters).
   */
  const toggleIso = async (aff: Affiliate) => {
    const next = !aff.isInternalIso;
    setIsoBusyId(aff.id);
    setAffiliates((prev) => prev.map((a) => (a.id === aff.id ? { ...a, isInternalIso: next } : a)));
    try {
      await api.affiliates.admin.setIsoStatus(aff.id, { isInternalIso: next });
    } catch (err: any) {
      setAffiliates((prev) => prev.map((a) => (a.id === aff.id ? { ...a, isInternalIso: !next } : a)));
      setIsoError(err?.message || 'Could not change that partner\'s ISO access.');
    } finally {
      setIsoBusyId(null);
    }
  };

  const filteredAffiliates = affiliates.filter(a => {
    const matchesSearch = !searchTerm ||
      a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.referralCode.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || a.status === statusFilter;
    const matchesKind = kindFilter === 'iso' ? a.isInternalIso === true
      : kindFilter === 'referral' ? !a.isInternalIso
      : true;
    return matchesSearch && matchesStatus && matchesKind;
  });

  const isoCount = affiliates.filter((a) => a.isInternalIso).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-black -m-3 md:-m-6">
        <div className="bg-black">
          <div className="px-4 md:px-6 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <UserPlus size={24} className="text-white" />
              <div>
                <h1 className="text-base md:text-lg font-semibold text-white">Partners</h1>
                <p className="text-[11px] text-gray-400">Referral partners and internal ISOs</p>
              </div>
            </div>
          </div>
          <div style={{ background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)', height: '3px' }} />
        </div>
        <RubyLoader variant="page" className="bg-stone-100 min-h-[calc(100vh-72px)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black -m-3 md:-m-6">
      {/* Header - Full width black banner (Page Header Standard) */}
      <div className="bg-black">
        <div className="px-4 md:px-6 py-3 flex items-center justify-between gap-4">
          {/* Left Zone: Icon + Title */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <UserPlus size={24} className="text-white" />
            <div>
              <h1 className="text-base md:text-lg font-semibold text-white">Partners</h1>
              <p className="text-[11px] text-gray-400">Referral partners and internal ISOs</p>
            </div>
          </div>

          {/* Center Zone: Stats - Desktop only */}
          <div className="hidden md:flex items-center gap-6">
            <div className="text-center">
              <div className="text-xl font-bold text-white">{analytics?.overview?.totalAffiliates ?? analytics?.totalAffiliates ?? 0}</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">Affiliates</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-emerald-400">{analytics?.overview?.activeAffiliates ?? analytics?.activeAffiliates ?? 0}</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">Active</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-blue-400">{analytics?.referrals?.total ?? analytics?.totalReferrals ?? 0}</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">Referrals</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-rose-400">{analytics?.referrals?.conversionRate ?? '0'}%</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">Conv Rate</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-amber-400">${((analytics?.earnings?.paid ?? analytics?.totalCommissionsPaid ?? 0) / 100).toFixed(0)}</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">Paid Out</div>
            </div>
          </div>

          {/* Right Zone: Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={fetchData}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowSettingsModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all min-h-[40px] bg-white text-black hover:bg-gray-100"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Settings</span>
            </button>
          </div>
        </div>
        {/* Gradient accent line - Amber for finance/affiliates */}
        <div
          style={{
            background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)',
            height: '3px'
          }}
        />
      </div>

      {/* Content area with light background */}
      <div className="bg-stone-100 min-h-[calc(100vh-72px)]">
        {/* Mobile Stats (hidden on desktop) */}
        <div className="md:hidden grid grid-cols-4 gap-2 p-4">
          <div className="p-3 rounded-xl bg-white border border-stone-200 text-center">
            <div className="text-lg font-bold text-stone-700">{analytics?.overview?.totalAffiliates ?? analytics?.totalAffiliates ?? 0}</div>
            <div className="text-[10px] text-stone-500">Affiliates</div>
          </div>
          <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
            <div className="text-lg font-bold text-emerald-600">{analytics?.overview?.activeAffiliates ?? analytics?.activeAffiliates ?? 0}</div>
            <div className="text-[10px] text-emerald-600/70">Active</div>
          </div>
          <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 text-center">
            <div className="text-lg font-bold text-blue-600">{analytics?.referrals?.total ?? analytics?.totalReferrals ?? 0}</div>
            <div className="text-[10px] text-blue-600/70">Referrals</div>
          </div>
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-center">
            <div className="text-lg font-bold text-amber-600">${((analytics?.earnings?.paid ?? analytics?.totalCommissionsPaid ?? 0) / 100).toFixed(0)}</div>
            <div className="text-[10px] text-amber-600/70">Paid</div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-4 md:py-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="font-medium text-red-600 hover:text-red-800">Dismiss</button>
            </div>
          )}

          {/* Affiliates List */}
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
            <div className="p-4 border-b border-stone-200">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <input
                    type="text"
                    placeholder="Search affiliates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 placeholder-stone-400 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="suspended">Suspended</option>
                  <option value="rejected">Rejected</option>
                </select>

                {/* Partner kind. Referral partners and internal ISOs share one
                    record and one portal, so this filters rather than routes. */}
                <select
                  value={kindFilter}
                  onChange={(e) => setKindFilter(e.target.value as '' | 'referral' | 'iso')}
                  className="px-3 py-2 min-h-[44px] rounded-lg border border-stone-300 bg-white text-sm text-stone-800"
                >
                  <option value="">All partners</option>
                  <option value="referral">Referral only</option>
                  <option value="iso">Internal ISO ({isoCount})</option>
                </select>
              </div>
            </div>

            {isoError && (
              <div role="alert" className="mx-4 mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span className="flex-1">{isoError}</span>
                <button onClick={() => setIsoError(null)} className="text-red-400 hover:text-red-600 font-bold px-1">×</button>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-stone-500 border-b border-stone-200 bg-stone-50">
                    <th className="px-4 py-3 font-medium">Affiliate</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Referral Code</th>
                    <th className="px-4 py-3 font-medium">Referrals</th>
                    <th className="px-4 py-3 font-medium">Conversions</th>
                    <th className="px-4 py-3 font-medium text-right">Earned</th>
                    <th className="px-4 py-3 font-medium">Internal ISO</th>
                    <th className="px-4 py-3 font-medium w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {filteredAffiliates.map((aff) => (
                    <tr
                      key={aff.id}
                      onClick={() => navigate(`/affiliates/${aff.id}`)}
                      className="text-sm hover:bg-stone-50 cursor-pointer group"
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-stone-800">{aff.name}</p>
                          <p className="text-stone-500 text-xs">{aff.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(aff.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <code className="bg-amber-50 border border-amber-200 px-2 py-1 rounded text-xs text-amber-700 font-medium">{aff.referralCode}</code>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(aff.referralCode);
                            }}
                            className={`p-1 rounded transition-colors ${copiedCode === aff.referralCode ? 'text-emerald-500 bg-emerald-50' : 'text-stone-400 hover:text-stone-600 hover:bg-stone-100'}`}
                            title="Copy code"
                          >
                            {copiedCode === aff.referralCode ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyReferralLink(aff.referralCode);
                            }}
                            className={`p-1 rounded transition-colors ${copiedLink === aff.referralCode ? 'text-emerald-500 bg-emerald-50' : 'text-stone-400 hover:text-stone-600 hover:bg-stone-100'}`}
                            title="Copy referral link"
                          >
                            {copiedLink === aff.referralCode ? <Check className="w-3.5 h-3.5" /> : <Link className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-stone-700">{aff.totalReferrals}</td>
                      <td className="px-4 py-3 text-stone-700">{aff.conversions}</td>
                      <td className="px-4 py-3 text-right text-stone-800 font-medium">${(aff.totalEarnings ?? 0).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        {/* stopPropagation: the row navigates, and flipping a
                            switch must not also open the partner. */}
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!aff.isInternalIso}
                          aria-label={`${aff.isInternalIso ? 'Remove' : 'Grant'} internal ISO access for ${aff.name}`}
                          disabled={isoBusyId === aff.id}
                          onClick={(e) => { e.stopPropagation(); toggleIso(aff); }}
                          title={aff.status !== 'approved' && !aff.isInternalIso
                            ? 'Approve this partner before giving them ISO access'
                            : aff.isInternalIso
                              ? 'Internal ISO — can be assigned merchant orders'
                              : 'Referral partner only'}
                          className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${
                            aff.isInternalIso ? 'bg-[#DC143C]' : 'bg-stone-300'
                          }`}
                        >
                          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
                            aff.isInternalIso ? 'left-[22px]' : 'left-0.5'
                          }`} />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <ChevronRight className="w-4 h-4 text-stone-400 group-hover:text-stone-600 transition-colors" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredAffiliates.length === 0 && (
                <div className="text-center py-12 text-stone-400">
                  No partners match those filters
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && settings && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full border border-stone-200 shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-stone-800 mb-4">Affiliate Program Settings</h2>

            {/* Program Status */}
            <div className="mb-6 p-4 bg-stone-50 rounded-xl border border-stone-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-stone-800">Program Status</h3>
                  <p className="text-xs text-stone-500">Enable or disable the affiliate program</p>
                </div>
                <button
                  onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                  className={`relative w-12 h-6 rounded-full transition-colors ${settings.enabled ? 'bg-emerald-500' : 'bg-stone-300'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${settings.enabled ? 'translate-x-7' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {/* Commission Mode */}
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Commission Type</label>
                <select
                  value={settings.commissionMode}
                  onChange={(e) => setSettings({ ...settings, commissionMode: e.target.value as 'percentage' | 'flat' | 'both' })}
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                >
                  <option value="percentage">Percentage Only</option>
                  <option value="flat">Flat Fee Only</option>
                  <option value="both">Percentage + Flat Fee</option>
                </select>
              </div>

              {/* Commission Rate - show if percentage or both */}
              {(settings.commissionMode === 'percentage' || settings.commissionMode === 'both') && (
                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-1">Commission Rate (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.percentageRate}
                    onChange={(e) => setSettings({ ...settings, percentageRate: Number(e.target.value) })}
                    className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                  />
                  <p className="text-xs text-stone-400 mt-1">Percentage of first payment</p>
                </div>
              )}

              {/* Flat Fee - show if flat or both */}
              {(settings.commissionMode === 'flat' || settings.commissionMode === 'both') && (
                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-1">Flat Fee ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(settings.flatFee / 100).toFixed(2)}
                    onChange={(e) => setSettings({ ...settings, flatFee: Math.round(Number(e.target.value) * 100) })}
                    className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                  />
                  <p className="text-xs text-stone-400 mt-1">Fixed amount per conversion</p>
                </div>
              )}

              {/* Min Payout Amount */}
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Minimum Payout ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={(settings.minPayoutAmount / 100).toFixed(2)}
                  onChange={(e) => setSettings({ ...settings, minPayoutAmount: Math.round(Number(e.target.value) * 100) })}
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                />
                <p className="text-xs text-stone-400 mt-1">Affiliates must earn this amount before requesting payout</p>
              </div>

              {/* Clearing Days */}
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-1">Clearing Period (days)</label>
                <input
                  type="number"
                  min="0"
                  max="90"
                  value={settings.clearingDays || 30}
                  onChange={(e) => setSettings({ ...settings, clearingDays: Number(e.target.value) })}
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                />
                <p className="text-xs text-stone-400 mt-1">Days before commissions become available for withdrawal (protects against chargebacks)</p>
              </div>

              {/*
                "Trial Days for Referrals" was set here. Removed because trials retired 2026-08-28 — the offer is price phases on packages and devices:
                a referred customer gets the deal's pricing, not extra free
                days, so a longer number here would have bought them nothing.
              */}

              {/* Approval Mode */}
              <div className="pt-2 border-t border-stone-200">
                <label className="block text-sm font-medium text-stone-600 mb-2">Affiliate Approval</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-100 transition">
                    <input
                      type="radio"
                      name="approvalMode"
                      checked={settings.approvalMode === 'auto'}
                      onChange={() => setSettings({ ...settings, approvalMode: 'auto', autoApprove: true })}
                      className="text-amber-600 focus:ring-amber-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-stone-700">Auto-approve</span>
                      <p className="text-xs text-stone-500">New affiliates are approved immediately</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-100 transition">
                    <input
                      type="radio"
                      name="approvalMode"
                      checked={settings.approvalMode === 'manual'}
                      onChange={() => setSettings({ ...settings, approvalMode: 'manual', autoApprove: false })}
                      className="text-amber-600 focus:ring-amber-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-stone-700">Manual review</span>
                      <p className="text-xs text-stone-500">You must approve each affiliate application</p>
                    </div>
                  </label>
                  {/* The certification gate. Without this radio the 'exam' mode
                      could be stored but never selected here, so the settings
                      form rendered with NO option checked and one stray Save
                      silently reverted the program to manual. */}
                  <label className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-100 transition">
                    <input
                      type="radio"
                      name="approvalMode"
                      checked={settings.approvalMode === 'exam'}
                      onChange={() => setSettings({ ...settings, approvalMode: 'exam', autoApprove: false })}
                      className="text-amber-600 focus:ring-amber-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-stone-700">Certification required</span>
                      <p className="text-xs text-stone-500">
                        New affiliates stay pending until they complete the training and pass
                        the certification exam. Their referral link activates automatically on
                        a pass — no manual approval step. Existing approved affiliates are
                        unaffected.
                      </p>
                    </div>
                  </label>

                  {/* Shown BEFORE the choice takes effect — it is rendered
                      whether or not 'Certification required' is currently
                      selected, so an admin reads it while deciding rather
                      than discovering the consequence after saving. Never a
                      window.confirm: this app is also viewed in an iOS
                      webview, where native confirm silently no-ops. Absent
                      entirely when the catalogue could not be loaded, so a
                      failed fetch never reads as "all clear". */}
                  {noActivatingCertification && (
                    <div className={`flex gap-2 rounded-lg border p-3 ${
                      settings.approvalMode === 'exam'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-amber-50 border-amber-200'
                    }`}
                    >
                      <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                        settings.approvalMode === 'exam' ? 'text-red-500' : 'text-amber-500'
                      }`}
                      />
                      <div>
                        <p className={`text-xs font-medium ${
                          settings.approvalMode === 'exam' ? 'text-red-700' : 'text-amber-700'
                        }`}
                        >
                          {settings.approvalMode === 'exam'
                            ? 'No certification can activate a partner right now'
                            : '"Certification required" is not ready yet'}
                        </p>
                        <p className="text-xs text-stone-600 mt-1">
                          No published certification is marked as the one that switches a
                          partner&apos;s referral link on. With this mode saved, every new
                          affiliate stays pending forever — passing an exam cannot activate
                          them, and only a manual admin override will. Set it on the
                          Certification portal (Exam settings &rarr; &ldquo;Activates
                          partners&rdquo;), then publish that certification.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="flex-1 px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-sm font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateSettings}
                disabled={actionLoading === 'settings'}
                className="flex-1 px-4 py-2.5 bg-black hover:bg-stone-800 text-white rounded-xl text-sm font-medium transition disabled:opacity-50"
              >
                {actionLoading === 'settings' ? <span className="flex justify-center"><RubyGemMark size={20} drawing color="currentColor" /></span> : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminAffiliates;
